import { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { API, ALL_OPERATIONS, OPERATION_NAMES, SURFACE_PROCESSES, AuthContext, useProcesses } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, Pencil, DownloadSimple, UploadSimple, CheckCircle, WarningCircle, MagnifyingGlass, ListChecks } from "@phosphor-icons/react";
import React from "react";
import CustomerSelect from "../components/CustomerSelect";
import SearchableSelect from "../components/SearchableSelect";

function ItemMaster() {
  const { user } = useContext(AuthContext);
  const { allOps, opNames, processes: pmProcesses } = useProcesses();
  const isAdmin = user?.role === "Admin" || user?.role === "Creator";
  const [parts, setParts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [listTab, setListTab] = useState("raw_material"); // "raw_material" | "job_work"
  const [searchQuery, setSearchQuery] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [boughtOutItems, setBoughtOutItems] = useState([]);
  // BOM Master grades — populates the RM Grade dropdown and autofills
  // density + rm_rate when a known grade is picked.
  const [bomMasterGrades, setBomMasterGrades] = useState([]);

  const emptyForm = {
    part_no: "", company_part_no: "", description: "", company_part_no_description: "", customer: "", part_rate: "",
    gross_weight: "", finished_weight: "",
    rm_grade: "", rm_grade_suffix: "", rm_type: "", outer_diameter: "", inner_diameter: "",
    diameter: "", length: "", width: "", height: "", thickness: "",
    density: "", rm_rate: "", rm_stock_kg: "",
    operation_rates: {}, opening_balances: {},
    operations_enabled: { cutting: true, incoming_store: true, machining_p1: true, machining_p2: true, semi_firewall: true, firewall: true, dispatch: true },
    // Per-op category — rolls up into Dashboard tiles (Machining Balance,
    // Daily Review Sheet buckets). Values: "machined" | "s_f" | "rfd" | "".
    // Empty string means "off / uncategorised" and is excluded from roll-ups.
    op_categories: {},
    process_config: {},
    scheduled_qty: "", machining_location: "",
    stock_at_supplier: [], is_critical: false,
    part_type: "raw_material", job_work_customer: "", job_work_operations: [],
    job_work_opening_balance: "",
    operations_flow: [],
    enforce_flow_strict: false,
    // ── Feb 2026 restructure: per-part process routing ─────────────────
    // Ordered list of operation rows, each = {
    //   op_no, process_code, rate, opening_balance,
    //   input_source: "rm" | "outsource" | "<other_process_code>",
    //   output_unit: "KG" | "Nos" | "Litres" | "Set"
    // }
    // When saving, we ALSO project this back into the legacy
    // `operations_enabled / operation_rates / opening_balances` shape so
    // every downstream tool (Operations page, DRS, RM Planning, reports)
    // that still reads those fields keeps working unchanged.
    op_routing: [],
    bom: [],
    assembly_yield: 1,
  };

  const [formData, setFormData] = useState(emptyForm);

  // ── Add-Part DRAFT persistence ───────────────────────────────────────
  // Stash unsaved Add-Part formData in sessionStorage so closing the dialog
  // (X / outside-click / Escape / page nav) does NOT lose the entry.
  // Only Cancel button, successful Save, or "Discard Draft" clears the draft.
  // Edit mode is intentionally NOT persisted — Edit always loads fresh.
  const DRAFT_KEY = "itemMasterDraft_v1";
  const hasDraft = () => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return false;
      const d = JSON.parse(raw);
      // Consider it a real draft only if user typed at least 1 char into a key field
      return !!(d && (d.part_no || d.description || d.customer || d.company_part_no));
    } catch { return false; }
  };
  const saveDraft = (d) => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* ignore quota errors */ }
  };
  const clearDraft = () => {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
  };
  // Persist every formData change while NOT in Edit mode.
  useEffect(() => {
    if (!editingPart && isOpen) saveDraft(formData);
  }, [formData, editingPart, isOpen]);

  // Excel Import state
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchParts(); fetchSuppliers();
    // Load bought-out master once — cheap dropdown source for BOM builder
    axios.get(`${API}/bought-out`).then(r => setBoughtOutItems(r.data)).catch(() => {});
    // Load BOM Master grades so the RM Grade dropdown has content on first
    // render. Cheap one-shot; auto-refetched when Customer Master fires
    // change events (BOM Master doesn't emit its own event yet, but the
    // list will refresh on modal re-open via editingPart change).
    axios.get(`${API}/bom-master`).then(r => setBomMasterGrades(r.data || [])).catch(() => {});
    const interval = setInterval(fetchParts, 20000);
    return () => clearInterval(interval);
  }, []);

  // Refresh BOM Master grades whenever the edit modal opens (cheap call,
  // ensures newly-added grades appear without a page refresh).
  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/bom-master`).then(r => setBomMasterGrades(r.data || [])).catch(() => {});
    }
  }, [isOpen]);

  const fetchSuppliers = async () => {
    try { setSuppliers((await axios.get(`${API}/suppliers`)).data); } catch {}
  };

  const fetchParts = async () => {
    try { setParts((await axios.get(`${API}/parts`)).data); }
    catch { toast.error("Failed to fetch parts"); }
  };

  // RM auto-calculation → result becomes gross_weight (Kg).
  // Density is stored in **Kg/cm³** (Steel ≈ 0.00785). Dimensions are in mm
  // → convert to cm (÷10) and volume_cm³ × density_kg_per_cm³ = weight in Kg.
  const calculatedWeight = (() => {
    let vol = 0;  // cm³
    const d = parseFloat(formData.density) || 0;
    if (formData.rm_type === "Pipe") {
      const od = (parseFloat(formData.outer_diameter) || 0) / 10;
      const id = (parseFloat(formData.inner_diameter) || 0) / 10;
      const l = (parseFloat(formData.length) || 0) / 10;
      vol = Math.PI * ((od/2)**2 - (id/2)**2) * l;
    } else if (formData.rm_type === "Rod") {
      const dia = (parseFloat(formData.diameter) || 0) / 10;
      const l = (parseFloat(formData.length) || 0) / 10;
      vol = Math.PI * (dia/2)**2 * l;
    } else if (formData.rm_type === "Square") {
      vol = ((parseFloat(formData.length)||0)/10) * ((parseFloat(formData.width)||0)/10) * ((parseFloat(formData.height)||0)/10);
    } else if (formData.rm_type === "Sheet") {
      vol = ((parseFloat(formData.length)||0)/10) * ((parseFloat(formData.width)||0)/10) * ((parseFloat(formData.thickness)||0)/10);
    }
    return vol * d;   // cm³ × Kg/cm³ = Kg
  })();
  const rmValue = calculatedWeight * (parseFloat(formData.rm_rate) || 0);

  // Auto-set gross_weight from RM calculation
  useEffect(() => {
    if (calculatedWeight > 0) {
      setFormData(prev => ({ ...prev, gross_weight: calculatedWeight.toFixed(2) }));
    }
  }, [calculatedWeight]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Project op_routing → legacy dicts before submitting ──────────
    // Every downstream tool (Operations page, DRS, RM Planning) still
    // reads from operations_enabled / operation_rates / opening_balances.
    // Regenerate them from the routing table so both shapes stay in sync.
    // Also stamp the position-derived `op_no` (10, 20, 30…) so the backend
    // has the canonical numbering — the UI derives it live, but persisting
    // it means downstream reports/exports see the same value.
    if (Array.isArray(formData.op_routing) && formData.op_routing.length) {
      formData.op_routing = formData.op_routing.map((row, idx) => ({
        ...row,
        op_no: (idx + 1) * 10,
      }));
      const enabled = {}, rates = {}, balances = {};
      for (const row of formData.op_routing) {
        if (!row.process_code) continue;
        enabled[row.process_code] = true;
        if (row.rate != null && row.rate !== "") rates[row.process_code] = Number(row.rate);
        if (row.opening_balance != null && row.opening_balance !== "") balances[row.process_code] = Number(row.opening_balance);
      }
      formData.operations_enabled = { ...formData.operations_enabled, ...enabled };
      formData.operation_rates = { ...formData.operation_rates, ...rates };
      formData.opening_balances = { ...formData.opening_balances, ...balances };
    }

    const submitData = {
      part_no: formData.part_no,
      // Company Part No. — optional internal grouping code. When left blank
      // we send null and the backend treats this row as "not pooled" (legacy
      // behaviour). When set, multiple parts sharing the same value pool
      // their shop-floor stock together.
      company_part_no: (formData.company_part_no || "").trim() || null,
      // Company-Part-No description only makes sense when a Company Part
      // No is set — otherwise force null so the two fields stay in sync.
      company_part_no_description: ((formData.company_part_no || "").trim()
        ? (formData.company_part_no_description || "").trim() || null
        : null),
      description: formData.description, customer: formData.customer,
      part_rate: formData.part_rate === "" ? null : parseFloat(formData.part_rate),
      // Preserve drawing metadata edited in Part Master — never overwrite
      // from BOM Routing (fields are managed on the Part Master page).
      drawing_no: editingPart?.drawing_no ?? null,
      revision_no: editingPart?.revision_no ?? null,
      gross_weight: formData.gross_weight === "" ? null : parseFloat(formData.gross_weight),
      finished_weight: formData.finished_weight === "" ? null : parseFloat(formData.finished_weight),
      rm_grade: formData.rm_grade || null, rm_grade_suffix: formData.rm_grade_suffix || null, rm_type: formData.rm_type || null,
      outer_diameter: formData.outer_diameter === "" ? null : parseFloat(formData.outer_diameter),
      inner_diameter: formData.inner_diameter === "" ? null : parseFloat(formData.inner_diameter),
      diameter: formData.diameter === "" ? null : parseFloat(formData.diameter),
      length: formData.length === "" ? null : parseFloat(formData.length),
      width: formData.width === "" ? null : parseFloat(formData.width),
      height: formData.height === "" ? null : parseFloat(formData.height),
      thickness: formData.thickness === "" ? null : parseFloat(formData.thickness),
      density: formData.density === "" ? null : parseFloat(formData.density),
      rm_rate: formData.rm_rate === "" ? null : parseFloat(formData.rm_rate),
      rm_stock_kg: formData.rm_stock_kg === "" ? null : parseFloat(formData.rm_stock_kg),
      operation_rates: formData.operation_rates,
      opening_balances: formData.opening_balances,
      operations_enabled: formData.operations_enabled,
      op_categories: formData.op_categories || {},
      // NEW authoritative routing (source of truth for the UI). We ALSO
      // keep the legacy dicts above in sync from `op_routing` (see the
      // block right before we submit) so nothing downstream breaks.
      op_routing: formData.op_routing || [],
      process_config: formData.process_config,
      scheduled_qty: formData.scheduled_qty === "" ? null : parseFloat(formData.scheduled_qty),
      machining_location: formData.machining_location || null,
      stock_at_supplier: formData.stock_at_supplier || [],
      is_critical: formData.is_critical || false,
      part_type: formData.part_type || "raw_material",
      job_work_customer: formData.part_type === "job_work" ? (formData.job_work_customer || formData.customer || "") : null,
      job_work_operations: formData.part_type === "job_work" ? (formData.job_work_operations || []) : null,
      job_work_opening_balance: formData.part_type === "job_work" && formData.job_work_opening_balance !== ""
        ? parseFloat(formData.job_work_opening_balance) : 0,
      operations_flow: (formData.operations_flow || []).filter(Boolean),
      enforce_flow_strict: !!formData.enforce_flow_strict,
      bom: formData.part_type === "assembly"
        ? (formData.bom || []).filter(b => {
            if (!(parseFloat(b.qty_per_assembly) > 0)) return false;
            const t = b.component_type || "manufactured";
            return t === "bought_out" ? !!b.component_bought_out_id : !!b.component_part_id;
          })
        : null,
      assembly_yield: formData.part_type === "assembly"
        ? (parseFloat(formData.assembly_yield) || 1)
        : null,
    };
    try {
      if (editingPart) {
        await axios.put(`${API}/parts/${editingPart.id}`, submitData);
        toast.success("Part updated");
      } else {
        await axios.post(`${API}/parts`, submitData);
        toast.success("Part created");
      }
      fetchParts(); setIsOpen(false); setEditingPart(null); setFormData(emptyForm); clearDraft();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save part");
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      part_no: part.part_no || "",
      company_part_no: part.company_part_no || "",
      description: part.description || "",
      company_part_no_description: part.company_part_no_description || "",
      customer: part.customer || "",
      part_rate: part.part_rate ?? "", gross_weight: part.gross_weight ?? "",
      finished_weight: part.finished_weight ?? "",
      rm_grade: part.rm_grade || "", rm_grade_suffix: part.rm_grade_suffix || "", rm_type: part.rm_type || "",
      outer_diameter: part.outer_diameter ?? "", inner_diameter: part.inner_diameter ?? "",
      diameter: part.diameter ?? "", length: part.length ?? "",
      width: part.width ?? "", height: part.height ?? "", thickness: part.thickness ?? "",
      density: part.density ?? "", rm_rate: part.rm_rate ?? "", rm_stock_kg: part.rm_stock_kg ?? "",
      operation_rates: part.operation_rates || {},
      opening_balances: part.opening_balances || {},
      operations_enabled: part.operations_enabled || { cutting: true, incoming_store: true, machining_p1: true, machining_p2: true, semi_firewall: true, firewall: true, dispatch: true },
      op_categories: part.op_categories || {},
      process_config: part.process_config || {},
      machining_location: part.machining_location || "",
      scheduled_qty: part.scheduled_qty ?? "",
      stock_at_supplier: part.stock_at_supplier || [],
      is_critical: part.is_critical || false,
      part_type: part.part_type || "raw_material",
      job_work_customer: part.job_work_customer || "",
      job_work_operations: part.job_work_operations || [],
      job_work_opening_balance: part.job_work_opening_balance ?? "",
      operations_flow: part.operations_flow || [],
      enforce_flow_strict: !!part.enforce_flow_strict,
      // ── op_routing hydration ──
      // Prefer the saved routing table. Otherwise, migrate live from
      // legacy operations_enabled + operation_rates + opening_balances
      // (indexed by ProcessMaster sequence via `allOps`) so existing
      // parts show something meaningful the moment BOM Routing opens.
      op_routing: (() => {
        if (Array.isArray(part.op_routing) && part.op_routing.length) return part.op_routing;
        const enabled = part.operations_enabled || {};
        const rates = part.operation_rates || {};
        const balances = part.opening_balances || {};
        const orderedCodes = allOps.filter(c => c !== "outsource" && enabled[c]);
        return orderedCodes.map((code, idx) => {
          const proc = (pmProcesses || []).find(p => p.code === code) || {};
          return {
            op_no: idx + 1,
            process_code: code,
            rate: rates[code] ?? proc.rate ?? "",
            opening_balance: balances[code] ?? "",
            // First operation eats raw material; the rest chain from the
            // previous op's output. Users can flip this after migration.
            input_source: idx === 0 ? "rm" : orderedCodes[idx - 1],
            output_unit: proc.output_unit || "Nos",
          };
        });
      })(),
      bom: part.bom || [],
      assembly_yield: part.assembly_yield ?? 1
    });
    setActiveTab("basic");
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this part and all its transactions?")) return;
    try { await axios.delete(`${API}/parts/${id}`); toast.success("Part deleted"); fetchParts(); }
    catch { toast.error("Failed to delete"); }
  };

  const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const setDictField = (dictKey, subKey, val) => setFormData(prev => ({
    ...prev, [dictKey]: { ...prev[dictKey], [subKey]: val }
  }));

  // Excel Import handlers
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/items/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Item_Master_Template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Failed to download template"); }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/items/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `Item_Master_Export_${stamp}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported · same template as import");
    } catch { toast.error("Export failed"); }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please select an Excel file (.xlsx)");
      return;
    }
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/items/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      const total = (res.data.new_imported || 0) + (res.data.updated || 0);
      if (total > 0) {
        toast.success(`Imported ${res.data.new_imported} new, updated ${res.data.updated} parts`);
        fetchParts();
      }
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} rows had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadErrorReport = async () => {
    if (!importResult?.import_id) return;
    try {
      const res = await axios.get(`${API}/items/error-report/${importResult.import_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Import_Error_Report.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download error report"); }
  };

  const renderDimFields = () => {
    const t = formData.rm_type;
    if (t === "Pipe") return (
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs">Outer Diameter (mm)</Label><Input type="number" step="any" value={formData.outer_diameter} onChange={e => setField("outer_diameter", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Inner Diameter (mm)</Label><Input type="number" step="any" value={formData.inner_diameter} onChange={e => setField("inner_diameter", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Length (mm)</Label><Input type="number" step="any" value={formData.length} onChange={e => setField("length", e.target.value)} className="min-h-[44px]" /></div>
      </div>
    );
    if (t === "Rod") return (
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Diameter (mm)</Label><Input type="number" step="any" value={formData.diameter} onChange={e => setField("diameter", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Length (mm)</Label><Input type="number" step="any" value={formData.length} onChange={e => setField("length", e.target.value)} className="min-h-[44px]" /></div>
      </div>
    );
    if (t === "Square") return (
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs">Length (mm)</Label><Input type="number" step="any" value={formData.length} onChange={e => setField("length", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Width (mm)</Label><Input type="number" step="any" value={formData.width} onChange={e => setField("width", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Height (mm)</Label><Input type="number" step="any" value={formData.height} onChange={e => setField("height", e.target.value)} className="min-h-[44px]" /></div>
      </div>
    );
    if (t === "Sheet") return (
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs">Length (mm)</Label><Input type="number" step="any" value={formData.length} onChange={e => setField("length", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Width (mm)</Label><Input type="number" step="any" value={formData.width} onChange={e => setField("width", e.target.value)} className="min-h-[44px]" /></div>
        <div><Label className="text-xs">Thickness (mm)</Label><Input type="number" step="any" value={formData.thickness} onChange={e => setField("thickness", e.target.value)} className="min-h-[44px]" /></div>
      </div>
    );
    return null;
  };

  const tabs = [
    { key: "basic", label: "Basic Info & RM" },
    { key: "operations", label: "Operations" },
    { key: "stock_supplier", label: "Stock at Supplier" }
  ];

  return (
    <Layout title="BOM Routing">
      <div className="p-4 md:p-6 space-y-4 max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-2 border-b border-[#E2E8F0]">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951] flex-shrink-0">
                <ListChecks size={22} weight="fill" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{fontFamily: 'Chivo'}}>BOM Routing</h1>
                <p className="text-xs md:text-sm text-[#64748B] mt-0.5">Configure raw material, operations &amp; BOM for every part</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && <>
            <Button variant="outline" onClick={handleDownloadTemplate}
              className="min-h-[40px] border-[#1D2951] text-[#1D2951] hover:bg-[#F5F3FF]"
              data-testid="download-template-button">
              <DownloadSimple size={16} weight="bold" className="mr-1.5" /> Template
            </Button>
            <Button variant="outline" onClick={handleExportExcel}
              className="min-h-[40px] border-[#D4A843] text-[#B45309] hover:bg-[#FEF9E7]"
              data-testid="export-excel-button">
              <DownloadSimple size={16} weight="bold" className="mr-1.5" /> Export
            </Button>
            <input type="file" accept=".xlsx,.xls" ref={fileInputRef} onChange={handleUploadExcel}
              className="hidden" data-testid="excel-file-input" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}
              className="min-h-[40px] border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDFA]"
              data-testid="upload-excel-button">
              <UploadSimple size={16} weight="bold" className="mr-1.5" /> {importing ? "Importing…" : "Upload"}
            </Button>
            </>}
            {isAdmin && <Dialog open={isOpen} onOpenChange={(open) => {
                setIsOpen(open);
                // On CLOSE: keep draft alive — only Cancel/Save/Discard clears it.
                // Edit mode clears its in-memory state so re-opening "+ Add" doesn't show edit data.
                if (!open && editingPart) { setEditingPart(null); setFormData(emptyForm); }
              }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1D2951] hover:bg-[#243766] text-white min-h-[40px] shadow-[0_4px_12px_-2px_rgba(29,41,81,0.4)]"
                onClick={() => {
                  // Resume draft (if any) for new entries; ignored on Edit.
                  try {
                    const raw = sessionStorage.getItem(DRAFT_KEY);
                    if (raw) {
                      const d = JSON.parse(raw);
                      setFormData({ ...emptyForm, ...d, part_type: d.part_type || listTab });
                      return;
                    }
                  } catch { /* ignore parse errors */ }
                  setFormData({ ...emptyForm, part_type: listTab });
                }}
                data-testid="add-part-button">
                <Plus size={18} weight="bold" className="mr-2" /> Add {listTab === "job_work" ? "Job Work" : "Raw Material"} Part
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-[#1D2951] flex items-center gap-3">
                  {editingPart ? "Edit Part" : (hasDraft() ? "Add Part — Resume Draft" : "Add Part")}
                  {!editingPart && hasDraft() && (
                    <button type="button"
                      onClick={() => { clearDraft(); setFormData({ ...emptyForm, part_type: listTab }); toast.success("Draft discarded"); }}
                      className="text-xs font-medium text-red-600 hover:text-red-800 underline normal-case"
                      data-testid="discard-draft-btn">
                      Discard Draft
                    </button>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="flex gap-1 border-b border-[#E2E8F0] mb-4">
                {tabs.map(t => (
                  <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-[#F5DF4D] text-[#1D2951]' : 'border-transparent text-[#64748B] hover:text-[#1D2951]'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                {activeTab === "basic" && (
                  <div className="space-y-4">
                    {/* Load-from-Part-Master shortcut — surfaces only when
                        adding a NEW row (no `editingPart`). Selecting an
                        existing part copies its identifier + commercial
                        fields so the operator only has to configure RM &
                        operations. Useful when re-registering a variant
                        or seeding routing on top of an existing part. */}
                    {!editingPart && (
                      <div className="p-3 bg-[#F8FAFC] border border-dashed border-[#CBD5E1] rounded">
                        <Label className="text-xs font-semibold text-[#1D2951] flex items-center gap-2">
                          Load from Part Master <span className="text-[10px] text-[#64748B] font-normal">(optional — auto-fills identity below)</span>
                        </Label>
                        <SearchableSelect
                          value=""
                          onChange={(id) => {
                            const p = parts.find(x => x.id === id);
                            if (!p) return;
                            setFormData(prev => ({
                              ...prev,
                              part_no: p.part_no || "",
                              customer: p.customer || "",
                              description: p.description || "",
                              company_part_no: p.company_part_no || "",
                              company_part_no_description: p.company_part_no_description || "",
                              finished_weight: p.finished_weight ?? "",
                              part_rate: p.part_rate ?? "",
                            }));
                            toast.success(`Loaded '${p.part_no}' from Part Master`);
                          }}
                          options={parts.map(p => ({
                            value: p.id,
                            label: p.part_no,
                            hint: `${p.customer || ""}${p.description ? " · " + p.description : ""}`,
                          }))}
                          placeholder="Pick an existing part…"
                          className="mt-1"
                          allowClear={false}
                          testId="load-from-part-master"
                        />
                      </div>
                    )}
                    {/* Core Part Info — order paired: each Part No is
                        IMMEDIATELY followed by its own Description field so
                        the operator sees which description belongs to which
                        part no at a glance. */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold">Customer Part No *</Label>
                        <Input data-testid="part-no-input" value={formData.part_no} onChange={e => setField("part_no", e.target.value)} required className="min-h-[44px]" placeholder="As printed on customer's PO" />
                      </div>
                      <div><Label className="text-xs font-semibold">Customer *</Label>
                        <CustomerSelect value={formData.customer}
                          onChange={(v) => setField("customer", v)}
                          testId="customer-input" /></div>
                    </div>
                    {/* Customer Part No Description — always shown, tied to
                        the Customer Part No above. This is the description as
                        printed on the customer's PO / drawing. */}
                    <div>
                      <Label className="text-xs font-semibold">Customer Part Description *</Label>
                      <Input data-testid="description-input" value={formData.description} onChange={e => setField("description", e.target.value)} required className="min-h-[44px]" placeholder="As printed on customer's PO / drawing" />
                    </div>
                    {/* Company Part No — OPTIONAL internal grouping code.
                        When empty the part is its own pool (no aggregation
                        with siblings). When set to a shared value across
                        rows, the shop floor treats them as one physical
                        part. The Company-Part-No Description below only
                        surfaces WHEN this field has a value. */}
                    <div>
                      <Label className="text-xs font-semibold">Company Part No <span className="text-[#94A3B8] font-normal">(optional — internal code)</span></Label>
                      <Input data-testid="company-part-no-input" value={formData.company_part_no} onChange={e => setField("company_part_no", e.target.value)} className="min-h-[44px]" placeholder="Leave blank if same as Customer Part No" />
                      <div className="text-[10px] text-[#64748B] mt-1">If multiple customer parts are physically identical, give them the SAME Company Part No. The shop-floor processes, RM, inventory and reports will then treat them as one pool.</div>
                    </div>
                    {/* Company Part No Description — CONDITIONAL. Only
                        rendered when the user has actually typed a Company
                        Part No above. Trimming ignores whitespace-only. */}
                    {(formData.company_part_no || "").trim() && (
                      <div>
                        <Label className="text-xs font-semibold">Company Part Description <span className="text-[#94A3B8] font-normal">(internal drawing / eng. description)</span></Label>
                        <Input data-testid="company-part-no-description-input"
                          value={formData.company_part_no_description}
                          onChange={e => setField("company_part_no_description", e.target.value)}
                          className="min-h-[44px]"
                          placeholder="Internal engineering description for this Company Part No" />
                      </div>
                    )}

                    {/* RM Calculator — only for raw_material parts (hidden for job_work + assembly) */}
                    {formData.part_type === "raw_material" && (
                    <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-[#1D2951]">Raw Material Calculator</h4>
                        {formData.rm_type && formData.rm_grade && (
                          <span className="text-xs font-mono font-bold text-[#7C3AED] bg-[#F5F3FF] px-3 py-1 rounded-lg border border-[#DDD6FE]" data-testid="rm-code-display">
                            RM Code: {(() => {
                              // Mirror the backend `compute_rm_code` layout:
                              // T-RM-{typePrefix}-{grade}[-{dims}]
                              const fmt = (v) => {
                                const f = parseFloat(v);
                                if (Number.isNaN(f)) return "";
                                return Number.isInteger(f) ? String(f) : String(f);
                              };
                              const tp = { Rod: "D", Square: "Sq", Sheet: "PL", Pipe: "P" }[formData.rm_type] || "XX";
                              let dim = "";
                              if (formData.rm_type === "Rod") {
                                dim = fmt(formData.diameter);
                              } else if (formData.rm_type === "Pipe") {
                                const od = fmt(formData.outer_diameter), id_ = fmt(formData.inner_diameter);
                                if (od && id_) dim = `${od}x${id_}`; else if (od) dim = od;
                              } else if (formData.rm_type === "Sheet") {
                                const w = fmt(formData.width), l = fmt(formData.length), t = fmt(formData.thickness);
                                dim = [w, l, t].filter(Boolean).join("x");
                              } else if (formData.rm_type === "Square") {
                                const s = fmt(formData.width || formData.thickness), l = fmt(formData.length);
                                if (s && l) dim = `${s}x${l}`; else if (s) dim = s;
                              }
                              const grade = formData.rm_grade || "";
                              return `T-RM-${tp}${grade ? `-${grade}` : ""}${dim ? `-${dim}` : ""}`;
                            })()}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">RM Grade</Label>
                          {/* BOM Master-backed picker. Type to filter, pick
                              to autofill density + rm_rate below. Users can
                              still type a grade that isn't in BOM Master
                              (backward-compat) — it just won't autofill. */}
                          <Input
                            list="bom-master-grades"
                            value={formData.rm_grade}
                            onChange={e => {
                              const val = e.target.value;
                              const match = bomMasterGrades.find(b => b.grade === val);
                              if (match) {
                                // Autofill density + rate + rm_type (best-guess mapping).
                                setFormData(prev => ({
                                  ...prev,
                                  rm_grade: val,
                                  density: match.density ?? prev.density,
                                  rm_rate: (match.total ?? match.rate ?? prev.rm_rate),
                                }));
                              } else {
                                setField("rm_grade", val);
                              }
                            }}
                            placeholder="Pick / type grade"
                            className="min-h-[40px]"
                            data-testid="rm-grade-input"
                          />
                          <datalist id="bom-master-grades">
                            {bomMasterGrades.map(b => (
                              <option key={b.id} value={b.grade}>{b.material_type} · ₹{(b.total ?? b.rate ?? 0)}</option>
                            ))}
                          </datalist>
                        </div>
                        <div>
                          <Label className="text-xs">RM Type</Label>
                          <Select value={formData.rm_type} onValueChange={(v) => setField("rm_type", v)}>
                            <SelectTrigger className="min-h-[40px]"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>{["Pipe","Rod","Square","Sheet"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">Density (Kg/cm³)</Label><Input type="number" step="any" value={formData.density} onChange={e => setField("density", e.target.value)} className="min-h-[40px]" /></div>
                      </div>
                      {formData.rm_type && renderDimFields()}
                      <div className="grid grid-cols-3 gap-3">
                        <div><Label className="text-xs">RM Rate (per kg)</Label><Input type="number" step="any" value={formData.rm_rate} onChange={e => setField("rm_rate", e.target.value)} className="min-h-[40px]" /></div>
                        {/* RM Stock field removed — pool stock is managed
                            from RM Log → "Add RM Stock". The legacy field
                            is still preserved in the DB. */}
                        {rmValue > 0 && (
                          <div className="flex items-end">
                            <div className="p-2 bg-white border rounded w-full">
                              <p className="text-xs text-[#64748B]">Raw Material Value</p>
                              <p className="text-lg font-bold font-mono text-[#10B981]">Rs.{rmValue.toFixed(2)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Job Work simplified panel — replaces RM Calculator when part_type=job_work */}
                    {formData.part_type === "job_work" && (
                      <div className="p-4 bg-[#F5F3FF] border border-[#C4B5FD] rounded-lg space-y-3" data-testid="jw-basic-panel">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-[#5B21B6]">Job Work Details</h4>
                          <span className="text-xs text-[#7C3AED] italic">Customer-supplied material — no RM calc.</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs font-semibold">RM Grade</Label>
                            <Input value={formData.rm_grade} onChange={e => setField("rm_grade", e.target.value)}
                              placeholder="e.g. EN8" className="min-h-[40px]" data-testid="jw-grade" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Finished Weight (kg)</Label>
                            <Input type="number" step="0.0001" value={formData.finished_weight}
                              onChange={e => setField("finished_weight", e.target.value)}
                              placeholder="Manual entry" className="min-h-[40px]" data-testid="jw-finished-wt" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Gross Weight (kg)</Label>
                            <Input type="number" step="0.0001" value={formData.gross_weight}
                              onChange={e => setField("gross_weight", e.target.value)}
                              placeholder="Manual entry" className="min-h-[40px]" data-testid="jw-gross-wt" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Weights & Rate — only show for raw_material; job_work uses panel above */}
                    {formData.part_type === "raw_material" && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs font-semibold">Gross Weight (kg) {calculatedWeight > 0 ? "(auto)" : ""}</Label>
                        <Input data-testid="gross-weight-input" type="number" step="0.0001"
                          value={formData.gross_weight} onChange={e => setField("gross_weight", e.target.value)}
                          className={`min-h-[44px] ${calculatedWeight > 0 ? 'bg-[#F0FDF4] font-bold' : ''}`} />
                        {calculatedWeight > 0 && <p className="text-xs text-[#10B981]">Auto from RM calc</p>}
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Finished Weight (kg)</Label>
                        <Input type="number" step="0.001" value={formData.finished_weight} onChange={e => setField("finished_weight", e.target.value)} className="min-h-[44px]" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold">Part Rate</Label>
                        <Input data-testid="part-rate-input" type="number" step="any" value={formData.part_rate} onChange={e => setField("part_rate", e.target.value)} className="min-h-[44px]" />
                      </div>
                    </div>
                    )}
                    {/* Part Rate (always visible for both types) */}
                    {formData.part_type === "job_work" && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs font-semibold">Part Rate (Job Work charge per piece)</Label>
                        <Input data-testid="part-rate-input" type="number" step="any" value={formData.part_rate} onChange={e => setField("part_rate", e.target.value)} className="min-h-[44px]" />
                      </div>
                    </div>
                    )}

                    <div className="flex items-center gap-4 pt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.is_critical} onCheckedChange={(v) => setField("is_critical", !!v)} data-testid="is-critical-checkbox" />
                        <Label className="text-xs font-semibold text-[#DC2626]">Set as Critical</Label>
                      </div>
                    </div>

                    {/* Assembly BOM Builder — only when part_type=assembly */}
                    {formData.part_type === "assembly" && (
                      <div className="p-4 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg space-y-3" data-testid="bom-builder">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-bold text-[#6D28D9]">Bill of Materials (BOM)</h4>
                          <span className="text-[10px] text-[#7C3AED] italic">Components consumed per 1 assembly produced</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs font-semibold">Finished Weight (kg) <span className="text-[#94A3B8] font-normal">(optional)</span></Label>
                            <Input type="number" step="0.0001" value={formData.finished_weight}
                              onChange={e => setField("finished_weight", e.target.value)}
                              className="min-h-[40px]" data-testid="asy-finished-wt" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Part Rate</Label>
                            <Input type="number" step="any" value={formData.part_rate}
                              onChange={e => setField("part_rate", e.target.value)}
                              className="min-h-[40px]" data-testid="asy-part-rate" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Assembly Yield</Label>
                            <Input type="number" step="any" value={formData.assembly_yield}
                              onChange={e => setField("assembly_yield", e.target.value)}
                              placeholder="1" className="min-h-[40px]" data-testid="asy-yield"
                              title="How many assemblies are produced per BOM execution. Almost always 1." />
                          </div>
                        </div>

                        {/* BOM lines */}
                        <div className="border border-[#DDD6FE] rounded bg-white overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-[#EDE9FE] text-[#6D28D9]">
                              <tr>
                                <th className="px-2 py-2 text-left font-bold w-24">Type</th>
                                <th className="px-2 py-2 text-left font-bold w-1/3">Component</th>
                                <th className="px-2 py-2 text-left font-bold">Description</th>
                                <th className="px-2 py-2 text-right font-bold w-24">Quantity / Assembly</th>
                                <th className="px-2 py-2 text-left font-bold w-32">Source Operation</th>
                                <th className="px-2 py-2 w-12"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(formData.bom || []).map((line, idx) => {
                                const cType = line.component_type || "manufactured";
                                const isBoughtOut = cType === "bought_out";
                                return (
                                <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`bom-row-${idx}`}>
                                  {/* Type toggle */}
                                  <td className="px-2 py-1">
                                    <select
                                      value={cType}
                                      onChange={e => {
                                        const nextType = e.target.value;
                                        const next = [...(formData.bom || [])];
                                        // Reset the previous picker's fields when switching type
                                        next[idx] = {
                                          ...next[idx],
                                          component_type: nextType,
                                          component_part_id: nextType === "manufactured" ? next[idx].component_part_id : "",
                                          component_bought_out_id: nextType === "bought_out" ? next[idx].component_bought_out_id : "",
                                          component_part_no: "",
                                          component_description: "",
                                        };
                                        setField("bom", next);
                                      }}
                                      className="w-full text-[10px] border border-[#CBD5E1] rounded px-1 py-1 min-h-[32px]"
                                      data-testid={`bom-type-${idx}`}
                                    >
                                      <option value="manufactured">Manufactured</option>
                                      <option value="bought_out">Bought Out</option>
                                    </select>
                                  </td>

                                  {/* Component picker — swaps based on type */}
                                  <td className="px-2 py-1">
                                    {!isBoughtOut ? (
                                      <SearchableSelect
                                        value={line.component_part_id || ""}
                                        onChange={(pid) => {
                                          const p = parts.find(pp => pp.id === pid);
                                          const next = [...(formData.bom || [])];
                                          next[idx] = { ...next[idx], component_part_id: pid, component_part_no: p?.part_no || "", component_description: p?.description || "" };
                                          setField("bom", next);
                                        }}
                                        options={parts.filter(p => p.id !== editingPart?.id).map(p => ({
                                          value: p.id,
                                          label: `${p.part_no}${p.company_part_no && p.company_part_no !== p.part_no ? ` (${p.company_part_no})` : ""}`,
                                          hint: p.description?.slice(0, 40) || "",
                                        }))}
                                        placeholder="pick part…"
                                        size="sm"
                                        testId={`bom-part-${idx}`}
                                      />
                                    ) : (
                                      <SearchableSelect
                                        value={line.component_bought_out_id || ""}
                                        onChange={(bid) => {
                                          const b = boughtOutItems.find(bb => bb.id === bid);
                                          const next = [...(formData.bom || [])];
                                          next[idx] = {
                                            ...next[idx],
                                            component_bought_out_id: bid,
                                            component_part_no: b?.item_code || "",
                                            component_description: b ? `${b.description}${b.make ? ' — ' + b.make : ''}` : "",
                                          };
                                          setField("bom", next);
                                        }}
                                        options={boughtOutItems.map(b => ({
                                          value: b.id,
                                          label: b.item_code,
                                          hint: `${b.description?.slice(0, 30) || ""}${b.make ? " — " + b.make : ""}`,
                                        }))}
                                        placeholder="pick bought-out…"
                                        size="sm"
                                        testId={`bom-bought-out-${idx}`}
                                      />
                                    )}
                                  </td>

                                  <td className="px-2 py-1 text-[#64748B] text-[11px]">{line.component_description || "—"}</td>
                                  <td className="px-2 py-1">
                                    <Input type="number" step="any" value={line.qty_per_assembly || ""}
                                      onChange={e => {
                                        const next = [...(formData.bom || [])];
                                        next[idx] = { ...next[idx], qty_per_assembly: e.target.value };
                                        setField("bom", next);
                                      }}
                                      className="min-h-[32px] text-xs text-right"
                                      data-testid={`bom-qty-${idx}`}
                                    />
                                  </td>
                                  <td className="px-2 py-1">
                                    {isBoughtOut ? (
                                      <span className="text-[10px] text-[#94A3B8] italic">N/A (bought-out)</span>
                                    ) : (
                                      <select
                                        value={line.consume_from_operation || "auto"}
                                        onChange={e => {
                                          const next = [...(formData.bom || [])];
                                          next[idx] = { ...next[idx], consume_from_operation: e.target.value };
                                          setField("bom", next);
                                        }}
                                        className="w-full text-xs border border-[#CBD5E1] rounded px-2 py-1 min-h-[32px]"
                                        data-testid={`bom-srcop-${idx}`}
                                      >
                                        <option value="auto">Auto (last op)</option>
                                        <option value="cutting">Cutting</option>
                                        <option value="incoming_store">Incoming Store</option>
                                        <option value="machining_p1">Machining P1</option>
                                        <option value="machining_p2">Machining P2</option>
                                        <option value="semi_firewall">Semi-Firewall</option>
                                        <option value="firewall">Firewall</option>
                                      </select>
                                    )}
                                  </td>
                                  <td className="px-2 py-1 text-center">
                                    <button type="button"
                                      onClick={() => {
                                        const next = (formData.bom || []).filter((_, i) => i !== idx);
                                        setField("bom", next);
                                      }}
                                      className="text-red-600 hover:text-red-800 text-sm font-bold"
                                      data-testid={`bom-remove-${idx}`}
                                      title="Remove">×</button>
                                  </td>
                                </tr>
                                );
                              })}
                              {(!formData.bom || formData.bom.length === 0) && (
                                <tr><td colSpan={6} className="px-2 py-3 text-center text-[#94A3B8] text-[11px] italic">No components yet. Click &quot;+ Add Component&quot; below.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <button type="button"
                          onClick={() => setField("bom", [...(formData.bom || []), { component_type: "manufactured", component_part_id: "", qty_per_assembly: "", consume_from_operation: "auto" }])}
                          className="text-xs font-bold text-[#6D28D9] hover:text-[#5B21B6]"
                          data-testid="bom-add-component">
                          + Add Component
                        </button>
                      </div>
                    )}

                    {/* Part Type: Raw Material vs Job Work vs Assembly */}
                    <div className="p-3 border border-[#E2E8F0] rounded bg-[#F8FAFC] space-y-3" data-testid="part-type-section">
                      <div className="flex items-center gap-6">
                        <Label className="text-xs font-bold text-[#1D2951]">Part Type:</Label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="part_type" value="raw_material"
                            checked={formData.part_type === "raw_material"}
                            onChange={() => setField("part_type", "raw_material")}
                            data-testid="part-type-rm" />
                          <span className="text-xs font-semibold">Raw Material (we buy RM & produce)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="part_type" value="job_work"
                            checked={formData.part_type === "job_work"}
                            onChange={() => setField("part_type", "job_work")}
                            data-testid="part-type-jw" />
                          <span className="text-xs font-semibold text-[#7C3AED]">Job Work (customer supplies material)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="part_type" value="assembly"
                            checked={formData.part_type === "assembly"}
                            onChange={() => setField("part_type", "assembly")}
                            data-testid="part-type-assembly" />
                          <span className="text-xs font-semibold text-[#6D28D9]">Assembly (BOM consumption)</span>
                        </label>
                      </div>
                      {formData.part_type === "job_work" && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-white rounded border border-[#C4B5FD]">
                          <div>
                            <Label className="text-[11px] font-semibold">Job Work Customer</Label>
                            <CustomerSelect
                              value={formData.job_work_customer}
                              onChange={(v) => setField("job_work_customer", v)}
                              testId="jw-customer" />
                            <p className="text-[10px] text-[#64748B] mt-1">Pick an existing customer or type a new one — it's saved to Customer Master.</p>
                          </div>
                          <div>
                            <Label className="text-[11px] font-semibold">Opening Balance (Nos)</Label>
                            <Input type="number" step="any" value={formData.job_work_opening_balance}
                              onChange={e => setField("job_work_opening_balance", e.target.value)}
                              className="min-h-[40px]" data-testid="jw-opening" />
                          </div>
                          <div className="md:col-span-3">
                            <Label className="text-[11px] font-semibold">Operations We Perform</Label>
                            <div className="flex flex-wrap gap-2 mt-1" data-testid="jw-ops">
                              {(formData.operations_enabled && Object.keys(formData.operations_enabled)).map(op => (
                                <label key={op} className="flex items-center gap-1 text-xs px-2 py-1 border border-[#E2E8F0] rounded cursor-pointer hover:bg-[#F1F5F9]">
                                  <input type="checkbox"
                                    checked={(formData.job_work_operations || []).includes(op)}
                                    onChange={(e) => {
                                      const cur = formData.job_work_operations || [];
                                      setField("job_work_operations", e.target.checked ? [...cur, op] : cur.filter(x => x !== op));
                                    }}
                                    data-testid={`jw-op-${op}`} />
                                  {op}
                                </label>
                              ))}
                            </div>
                            <p className="text-[10px] text-[#64748B] mt-1">Only selected operations will be allowed when issuing in the Job Work module.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Machining Info */}
                    <div className="p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded space-y-2">
                      <Label className="text-xs font-bold text-[#92400E]">Machining Plants</Label>
                      <p className="text-xs text-[#92400E]">By default, parts are enabled for both P1 & P2. Toggle in the Operations tab to change.</p>
                    </div>
                  </div>
                )}

                {activeTab === "operations" && (
                  <OpRoutingSection
                    routing={formData.op_routing || []}
                    setRouting={(list) => setField("op_routing", list)}
                    pmProcesses={pmProcesses}
                    opNames={opNames}
                    partId={editingPart?.id}
                  />
                )}

                {/* Legacy "Rates" tab removed — rates & opening balances are edited directly inside the Operations tab's Process Routing table. */}

                {activeTab === "stock_supplier" && (
                  <div className="space-y-3">
                    <p className="text-xs text-[#64748B]">Configure opening stock at each supplier. Supplier's processes auto-populate from their profile.</p>
                    {(formData.stock_at_supplier || []).map((entry, idx) => {
                      const sup = suppliers.find(s => s.name === entry.supplier);
                      const supProcesses = sup?.processes || [];
                      return (
                        <div key={idx} className="grid grid-cols-4 gap-3 items-end p-3 rounded border border-[#E2E8F0]">
                          <div>
                            <Label className="text-xs font-semibold">Supplier</Label>
                            <Select value={entry.supplier || ""} onValueChange={v => {
                              const arr = [...(formData.stock_at_supplier || [])];
                              const selSup = suppliers.find(s => s.name === v);
                              arr[idx] = { ...arr[idx], supplier: v, process: (selSup?.processes || [])[0] || "" };
                              setField("stock_at_supplier", arr);
                            }}>
                              <SelectTrigger className="min-h-[40px]"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Process</Label>
                            <Select value={entry.process || ""} onValueChange={v => {
                              const arr = [...(formData.stock_at_supplier || [])];
                              arr[idx] = { ...arr[idx], process: v };
                              setField("stock_at_supplier", arr);
                            }}>
                              <SelectTrigger className="min-h-[40px]"><SelectValue placeholder="Process" /></SelectTrigger>
                              <SelectContent>
                                {supProcesses.length > 0
                                  ? supProcesses.map(p => <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>)
                                  : SURFACE_PROCESSES.map(p => <SelectItem key={p} value={p}>{OPERATION_NAMES[p] || p}</SelectItem>)
                                }
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Opening Stock</Label>
                            <Input type="number" step="any" min="0" className="min-h-[40px]"
                              value={entry.opening_stock ?? ""}
                              onChange={e => {
                                const arr = [...(formData.stock_at_supplier || [])];
                                arr[idx] = { ...arr[idx], opening_stock: e.target.value === "" ? 0 : parseFloat(e.target.value) };
                                setField("stock_at_supplier", arr);
                              }} />
                          </div>
                          <Button type="button" variant="outline" size="sm" className="min-h-[40px] text-[#EF4444]"
                            onClick={() => {
                              const arr = [...(formData.stock_at_supplier || [])];
                              arr.splice(idx, 1);
                              setField("stock_at_supplier", arr);
                            }}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      );
                    })}
                    <Button type="button" variant="outline" size="sm"
                      className="border-[#1D2951] text-[#1D2951]"
                      onClick={() => setField("stock_at_supplier", [...(formData.stock_at_supplier || []), { supplier: "", process: "", opening_stock: 0 }])}>
                      <Plus size={14} className="mr-1" /> Add Supplier Stock
                    </Button>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit" className="flex-1 bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px]" data-testid="save-part-button">
                    {editingPart ? "Update Part" : "Create Part"}
                  </Button>
                  <Button type="button" variant="outline" className="flex-1 min-h-[44px]" onClick={() => {
                    // Cancel = explicit discard. Clears draft + closes dialog.
                    clearDraft();
                    setEditingPart(null);
                    setFormData(emptyForm);
                    setIsOpen(false);
                  }} data-testid="cancel-part-btn">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>}
          </div>
        </div>

        {/* Import Results */}
        {importResult && (
          <Card className="p-4 border border-[#E2E8F0] space-y-3" data-testid="import-result-card">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-[#1D2951]">Import Results</h3>
              <Button variant="ghost" size="sm" onClick={() => setImportResult(null)} className="text-xs text-[#64748B]">Dismiss</Button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-[#F8FAFC] rounded text-center">
                <p className="text-2xl font-bold font-mono text-[#1D2951]" data-testid="import-total-rows">{importResult.total_rows}</p>
                <p className="text-xs text-[#64748B]">Total Rows</p>
              </div>
              <div className="p-3 bg-[#F0FDF4] rounded text-center">
                <p className="text-2xl font-bold font-mono text-[#10B981]" data-testid="import-new-count">{importResult.new_imported}</p>
                <p className="text-xs text-[#10B981]">New Imported</p>
              </div>
              <div className="p-3 bg-[#EFF6FF] rounded text-center">
                <p className="text-2xl font-bold font-mono text-[#3B82F6]" data-testid="import-updated-count">{importResult.updated}</p>
                <p className="text-xs text-[#3B82F6]">Updated</p>
              </div>
              <div className="p-3 bg-[#FEF2F2] rounded text-center">
                <p className="text-2xl font-bold font-mono text-[#EF4444]" data-testid="import-failed-count">{importResult.failed}</p>
                <p className="text-xs text-[#EF4444]">Failed</p>
              </div>
            </div>
            {importResult.failed > 0 && importResult.import_id && (
              <div className="space-y-2">
                <Button variant="outline" size="sm" onClick={handleDownloadErrorReport}
                  className="text-[#EF4444] border-[#EF4444] hover:bg-red-50"
                  data-testid="download-error-report-button">
                  <DownloadSimple size={14} className="mr-1" /> Download Error Report
                </Button>
                {importResult.errors_preview?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {importResult.errors_preview.map((err, i) => (
                      <div key={i} className="flex gap-2 text-[#64748B]">
                        <span className="font-mono text-[#EF4444] shrink-0">Row {err.row}:</span>
                        <span>{err.part_no && <span className="font-semibold">[{err.part_no}]</span>} {err.reason}</span>
                      </div>
                    ))}
                    {importResult.failed > 10 && <p className="text-[#64748B] italic">...and {importResult.failed - 10} more. Download full error report.</p>}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        <Card className="border border-[#E2E8F0]">
          {/* Top-level tabs: split the list by part_type */}
          <div className="flex gap-1 border-b border-[#E2E8F0] bg-[#F8FAFC] px-3 pt-3">
            {(() => {
              const rmCount = parts.filter(p => (p.part_type || "raw_material") === "raw_material").length;
              const jwCount = parts.filter(p => p.part_type === "job_work").length;
              return (
                <>
                  <button
                    onClick={() => setListTab("raw_material")}
                    className={`px-5 py-2 text-sm font-bold rounded-t-md border-b-2 transition ${listTab === "raw_material" ? "border-[#F5DF4D] bg-white text-[#1D2951]" : "border-transparent text-[#64748B] hover:text-[#1D2951]"}`}
                    data-testid="list-tab-raw-material"
                  >
                    Raw Material
                    <span className="ml-2 text-xs bg-[#1D2951] text-white rounded-full px-2 py-0.5 font-mono">{rmCount}</span>
                  </button>
                  <button
                    onClick={() => setListTab("job_work")}
                    className={`px-5 py-2 text-sm font-bold rounded-t-md border-b-2 transition ${listTab === "job_work" ? "border-[#F5DF4D] bg-white text-[#1D2951]" : "border-transparent text-[#64748B] hover:text-[#1D2951]"}`}
                    data-testid="list-tab-job-work"
                  >
                    Job Work
                    <span className="ml-2 text-xs bg-[#7C3AED] text-white rounded-full px-2 py-0.5 font-mono">{jwCount}</span>
                  </button>
                  <button
                    onClick={() => setListTab("assembly")}
                    className={`px-5 py-2 text-sm font-bold rounded-t-md border-b-2 transition ${listTab === "assembly" ? "border-[#F5DF4D] bg-white text-[#1D2951]" : "border-transparent text-[#64748B] hover:text-[#1D2951]"}`}
                    data-testid="list-tab-assembly"
                  >
                    Assembly
                    <span className="ml-2 text-xs bg-[#6D28D9] text-white rounded-full px-2 py-0.5 font-mono">{parts.filter(p => p.part_type === "assembly").length}</span>
                  </button>
                </>
              );
            })()}
          </div>
          <div className="p-3 border-b border-[#E2E8F0]">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by Part No, Description, or Customer..."
                className="min-h-[40px] pl-9 text-sm"
                data-testid="item-search-input"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="parts-table">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Customer Part No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Company Part No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">RM Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Customer</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Part Rate</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Gross Wt</th>
                  <th className="px-4 py-3 text-right text-xs font-bold uppercase">Finished Wt</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Plant</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {parts.filter(p => {
                  // Tab split: Raw Material vs Job Work
                  const pt = p.part_type || "raw_material";
                  if (pt !== listTab) return false;
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  return (p.part_no || "").toLowerCase().includes(q) ||
                    (p.company_part_no || "").toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q) ||
                    (p.company_part_no_description || "").toLowerCase().includes(q) ||
                    (p.customer || "").toLowerCase().includes(q);
                }).map(part => (
                  <tr key={part.id} className="hover:bg-[#F8FAFC]" data-testid={`part-row-${part.part_no}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-[#1D2951]">
                      <div>{part.part_no}</div>
                      {part.description && (
                        <div className="text-[10px] font-normal text-[#64748B] mt-0.5 truncate max-w-[220px]" title={part.description}>{part.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#1D4ED8]">
                      {part.company_part_no && part.company_part_no !== part.part_no ? (
                        <>
                          <div>{part.company_part_no}</div>
                          {part.company_part_no_description && (
                            <div className="text-[10px] font-normal text-[#64748B] mt-0.5 truncate max-w-[220px]" title={part.company_part_no_description}>{part.company_part_no_description}</div>
                          )}
                        </>
                      ) : <span className="text-[#94A3B8]">—</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#7C3AED] font-bold">{part.rm_code || '-'}</td>
                    <td className="px-4 py-3 text-sm">{part.description}</td>
                    <td className="px-4 py-3 text-sm">{part.customer}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{part.part_rate ?? "-"}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{part.gross_weight ? parseFloat(part.gross_weight).toFixed(2) : "-"}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{part.finished_weight ?? "-"}</td>
                    <td className="px-4 py-3 text-center text-sm font-semibold text-[#1D2951]">{[part.operations_enabled?.machining_p1 && "P1", part.operations_enabled?.machining_p2 && "P2"].filter(Boolean).join(", ") || "-"}</td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(part)} className="min-h-[36px]" data-testid={`edit-part-${part.part_no}`}><Pencil size={14} /></Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(part.id)} className="min-h-[36px] text-[#EF4444] hover:bg-red-50" data-testid={`delete-part-${part.part_no}`}><Trash size={14} /></Button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parts.length === 0 && <div className="text-center py-12 text-[#64748B]">No parts added yet.</div>}
            {parts.length > 0 && parts.filter(p => (p.part_type || "raw_material") === listTab).length === 0 && (
              <div className="text-center py-12 text-[#64748B]">
                No {listTab === "job_work" ? "Job Work" : "Raw Material"} parts yet.
                <br/>
                <span className="text-xs italic">Click "Add Part" to create one — Part Type defaults to {listTab === "job_work" ? "Job Work" : "Raw Material"} on this tab.</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default ItemMaster;

// ────────────────────────────────────────────────────────────────────
// OpRoutingSection — per-part process routing table (Feb 2026 rework).
//
// Columns: Op # · Process Name · Process Rate · Opening Balance ·
//          Input Operation · Output Unit.
//
// Data shape (formData.op_routing): [{
//   op_no, process_code, rate, opening_balance, input_source, output_unit
// }, …]
//
// input_source values:
//   "rm"           — this op consumes raw material directly
//   "outsource"    — this op consumes stock coming back from an outsourcer
//   "<code>"       — this op consumes the output of another process on the
//                    same part (uses the process_code of the source row)
//
// output_unit matters because a subsequent op that expects "Nos" cannot
// naively consume "KG" — the UI flags the mismatch so operators notice.
// ────────────────────────────────────────────────────────────────────

function OpRoutingSection({ routing, setRouting, pmProcesses, opNames, partId }) {
  const list = Array.isArray(routing) ? routing : [];
  const procByCode = Object.fromEntries((pmProcesses || []).map(p => [p.code, p]));

  // Op # is now DERIVED from position: idx*10 + 10 → OP-10, OP-20, OP-30…
  // (Classic manufacturing convention with room to insert intermediate ops.)
  // No manual entry — reordering rows automatically renumbers because the
  // number is computed on every render.
  const opNoFor = (idx) => (idx + 1) * 10;

  const updateRow = (idx, patch) => {
    const next = list.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setRouting(next);
  };

  const removeRow = (idx) => {
    setRouting(list.filter((_, i) => i !== idx));
  };

  const moveRow = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    setRouting(next);
  };

  const addRow = () => {
    // Pick the first Process Master row that isn't already in the routing.
    const usedCodes = new Set(list.map(r => r.process_code));
    const nextProc = (pmProcesses || []).find(p => !usedCodes.has(p.code));
    // Previous row's process becomes the default input for the new row —
    // the classic chain assumption for shop-floor routing.
    const prev = list[list.length - 1];
    const defaultInput = list.length === 0 ? "rm" : (prev?.process_code || "rm");
    setRouting([...list, {
      // op_no isn't stored on the row anymore — it's derived from position.
      // We still snapshot the current number so back-end save carries it.
      process_code: nextProc?.code || "",
      rate: nextProc?.rate ?? "",
      opening_balance: "",
      input_source: defaultInput,
      output_unit: nextProc?.output_unit || "Nos",
    }]);
  };

  // When operator picks a different process for a row, autofill rate + output_unit
  // from Process Master (they can still override).
  const changeProcess = (idx, code) => {
    const p = procByCode[code] || {};
    updateRow(idx, {
      process_code: code,
      rate: p.rate ?? list[idx].rate,
      output_unit: p.output_unit || list[idx].output_unit,
    });
  };

  return (
    <div className="space-y-3" data-testid="op-routing-section">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-bold text-[#1D2951]">Process Routing</Label>
          <p className="text-[11px] text-[#64748B]">
            The manufacturing sequence for this part. Rate defaults from Process Master; override per row. Input marks whether the row consumes raw material, outsource stock, or another operation's output.
          </p>
        </div>
        <Button type="button" onClick={addRow} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1"
          data-testid="add-op-routing-btn">
          <Plus size={14} weight="bold" /> Add Process Routing
        </Button>
      </div>

      <div className="border border-[#E2E8F0] rounded overflow-hidden">
        <table className="w-full text-xs" data-testid="op-routing-table">
          <thead className="bg-[#F8FAFC] text-[#475569] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-2 py-2 text-center w-14">Operation #</th>
              <th className="px-3 py-2 text-left">Process Name</th>
              <th className="px-3 py-2 text-right">Rate (₹)</th>
              <th className="px-3 py-2 text-right">Opening Balance</th>
              <th className="px-3 py-2 text-left">Input From</th>
              <th className="px-3 py-2 text-center">Output Unit</th>
              <th className="px-2 py-2 text-center w-16">×</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {list.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-[#94A3B8] italic">
                No routing yet — click <b>Add Process Routing</b> to start building this part's sequence.
              </td></tr>
            )}
            {list.map((row, idx) => {
              const proc = procByCode[row.process_code] || {};
              // Flag mismatch: this row expects proc.input_unit but the
              // previous op's output_unit (or RM = KG) doesn't match.
              const prevOutput = row.input_source === "rm"
                ? "KG"
                : row.input_source === "outsource"
                  ? (proc.input_unit || "Nos")
                  : (list.find(r => r.process_code === row.input_source)?.output_unit || "Nos");
              const expected = proc.input_unit || "Nos";
              const unitMismatch = prevOutput && expected && prevOutput.toUpperCase() !== expected.toUpperCase();
              return (
                <tr key={idx} className="hover:bg-[#F8FAFC]">
                  <td className="px-2 py-2 text-center">
                    {/* Op # is auto-computed from position: (idx+1)×10 →
                        OP-10, OP-20, OP-30… Reorder the row and the number
                        instantly re-flows. No manual entry. */}
                    <div className="flex flex-col items-center gap-1">
                      <span className="inline-flex items-center px-2 py-1 rounded font-mono font-bold text-[11px] text-white bg-[#1D2951]"
                        data-testid={`opr-no-${idx}`}>
                        OP-{opNoFor(idx)}
                      </span>
                      <div className="flex gap-0.5">
                        <button type="button" onClick={() => moveRow(idx, -1)} className="hover:text-[#1D2951] text-[#94A3B8] px-0.5 text-[10px]" title="Move up">↑</button>
                        <button type="button" onClick={() => moveRow(idx, 1)} className="hover:text-[#1D2951] text-[#94A3B8] px-0.5 text-[10px]" title="Move down">↓</button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <select value={row.process_code || ""}
                      onChange={e => changeProcess(idx, e.target.value)}
                      className="w-full min-h-[34px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                      data-testid={`opr-proc-${idx}`}>
                      <option value="">— Select process —</option>
                      {(pmProcesses || []).map(p => (
                        <option key={p.code} value={p.code}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" value={row.rate ?? ""}
                      onChange={e => updateRow(idx, { rate: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="text-right font-mono text-xs min-h-[34px]"
                      placeholder={proc.rate != null ? String(proc.rate) : "—"}
                      data-testid={`opr-rate-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" value={row.opening_balance ?? ""}
                      onChange={e => updateRow(idx, { opening_balance: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      className="text-right font-mono text-xs min-h-[34px]"
                      placeholder="0"
                      data-testid={`opr-open-${idx}`} />
                  </td>
                  <td className="px-3 py-2">
                    <select value={row.input_source || "rm"}
                      onChange={e => updateRow(idx, { input_source: e.target.value })}
                      className="w-full min-h-[34px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
                      data-testid={`opr-input-${idx}`}>
                      <option value="rm">Raw Material</option>
                      <option value="outsource">Outsource</option>
                      {/* Every process from Process Master is a valid input
                          source, not just ones already in the routing —
                          shop-floor sometimes wants to reference a step
                          that hasn't been added as a row yet. */}
                      <optgroup label="Process Master">
                        {(pmProcesses || [])
                          .filter(p => p.code !== row.process_code && p.code !== "outsource")
                          .map(p => (
                            <option key={p.code} value={p.code}>{p.name}</option>
                          ))}
                      </optgroup>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center gap-1 justify-center">
                      <select value={row.output_unit || "Nos"}
                        onChange={e => updateRow(idx, { output_unit: e.target.value })}
                        className="min-h-[34px] border border-[#E2E8F0] rounded px-1.5 text-xs bg-white"
                        data-testid={`opr-out-${idx}`}>
                        {["KG", "Nos", "Litres", "Set"].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      {unitMismatch && (
                        <span title={`Input expected ${expected} but incoming is ${prevOutput}`}
                          className="text-[#B45309] text-[10px] font-bold" data-testid={`opr-mismatch-${idx}`}>
                          ⚠
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button type="button" onClick={() => removeRow(idx)}
                      className="text-[#DC2626] hover:bg-red-50 px-1.5 rounded text-sm"
                      title="Remove row" data-testid={`opr-remove-${idx}`}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-[#94A3B8] flex items-center gap-1">
        <span className="text-[#B45309] font-bold">⚠</span>
        marks a row where its process expects a different input unit than what's coming from the upstream row.
        {partId && <> · Editing routing for part <code className="font-mono">{partId.slice(0, 8)}</code></>}
      </p>
    </div>
  );
}
